<template>
  <v-container class="estimated-insurance-required-output-container">
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link
          v-if="
            testatorAgeAndIdCorrect &&
            !this.testator.enabledFeatures.includes('liquidity_calculator')
          "
          :to="{ name: 'GetFreeInsuranceQuote' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
        <router-link
          v-else
          :to="{ name: 'EstimatedInsuranceRequiredInput' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="11">
        <h1 class="mb-2">
          These are your estimated estate administration costs
        </h1>
      </v-col>
    </v-row>

    <v-row no-gutters class="mt-sm-6">
      <v-col cols="12" sm="3" offset-sm="1" class="form-blurb"> </v-col>

      <v-col cols="12" md="6" offset-md="2" sm="7" offset-sm="1">
        <div v-if="loading" class="text-center mt-10">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else>
          <v-row no-gutters class="fee-block">
            <v-col cols="7" sm="9">
              <h3>Executor fees ({{ executorFeePercentage }}% + VAT)</h3>
            </v-col>
            <v-col cols="5" sm="3">
              <span class="fee-amount"
                >R {{ estateCostCalculation.executorFees | currency }}</span
              >
            </v-col>
            <v-col cols="12" sm="9">
              <p>
                The fees your Executor is entitled to charge for the
                administration of your deceased estate. The maximum legal rate
                is 3.5% of the gross value of your estate plus VAT. The Executor
                is also entitled to 6% plus VAT on all income coming into the
                estate during the process of administration.
              </p>
              <p>
                <b
                  >Gross value of your estate:<br
                    v-if="$vuetify.breakpoint.xs"
                  />
                  R {{ estateCostCalculation.grossEstateValue | currency }}</b
                >
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters class="fee-block">
            <v-col cols="7" sm="9">
              <h3>Conveyancing fees</h3>
            </v-col>
            <v-col cols="5" sm="3">
              <span class="fee-amount"
                >R {{ estateCostCalculation.conveyancingFees | currency }}</span
              >
            </v-col>
            <v-col cols="12" sm="9">
              <p>
                Fees and taxes payable when a property is being transferred from
                one person (or their estate) to another. This does not include
                the outstanding amount on any mortgage that may be applicable.
              </p>
              <p>
                <b
                  >Total ownership value of your properties:<br
                    v-if="$vuetify.breakpoint.xs"
                  />
                  R
                  {{
                    estateCostCalculation.propertyOwnershipValue | currency
                  }}</b
                >
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters class="fee-block">
            <v-col cols="7" sm="9">
              <h3>Master's fees</h3>
            </v-col>
            <v-col cols="5" sm="3">
              <span class="fee-amount"
                >R {{ estateCostCalculation.mastersFees | currency }}</span
              >
            </v-col>
            <v-col cols="12" sm="9">
              <p>
                Fees payable to Master's Office pertaining to the administration
                of a deceased estate
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters class="fee-block">
            <v-col cols="7" sm="9">
              <h3>Standard administrative costs</h3>
            </v-col>
            <v-col cols="5" sm="3">
              <span class="fee-amount"
                >R
                {{
                  estateCostCalculation.standardAdministrativeCosts | currency
                }}</span
              >
            </v-col>
            <v-col cols="12" sm="9">
              <p>
                Fees towards paying for costs of advertising notices in
                newspapers regarding the deceased estate
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters class="fee-block">
            <v-col cols="7" sm="9">
              <h3>Estate Duty</h3>
            </v-col>
            <v-col cols="5" sm="3">
              <span class="fee-amount"
                >R {{ estateCostCalculation.estateDuty | currency }}</span
              >
            </v-col>
            <v-col cols="12" sm="9">
              <p>
                Taxes payable to the South African Revenue Service upon your
                passing. Includes R3.5m abatement amount
              </p>
              <p>
                <b
                  >Dutiable estate before 4A deduction:<br
                    v-if="$vuetify.breakpoint.xs"
                  />
                  R
                  {{
                    estateCostCalculation.dutiableEstateBefore4aDeduction
                      | currency
                  }}</b
                >
              </p>
            </v-col>
          </v-row>

          <v-divider />

          <v-row no-gutters class="mt-5">
            <v-col cols="12" sm="8">
              <h3>Estimated estate administration costs</h3>
            </v-col>
            <v-col
              cols="12"
              sm="4"
              :class="{ 'total-cover-required-xs': $vuetify.breakpoint.xs }"
              class="total-cover-required"
            >
              <span class="secondary-highlighted"
                >R
                {{
                  estateCostCalculation.estimatedEstateAdministrativeCosts
                    | currency
                }}</span
              >
            </v-col>
          </v-row>

          <div class="warning-text">
            <p>Please note:</p>
            <ul class="pl-0">
              <li class="warning-text">
                These values are illustrative of the costs based on the
                information you have provided and will change over time, review
                your will and estate at regular intervals
              </li>
              <li
                v-if="testator.maritalRegime === 'OUT_OF_COMMUNITY_ACCRUAL'"
                class="warning-text"
              >
                The estimated estate administration costs do not take into
                account adjustments for the accrual in terms of your marriage.
                Neither does it make provision for income tax or capital gains
                tax payable in the year of your passing.
              </li>
              <li v-else class="warning-text">
                The estimated estate administration costs do not make provision
                for income tax or capital gains tax payable in the year of your
                passing.
              </li>
              <li class="warning-text">
                If your estate does not have R
                {{
                  estateCostCalculation.estimatedEstateAdministrativeCosts
                    | currency
                }}
                cash to cover these costs plus any outstanding debts you may
                have, your assets will be sold in order to cover these costs.
              </li>
            </ul>
          </div>

          <div class="text-center text-sm-left">
            <v-btn
              v-if="
                testatorAgeAndIdCorrect &&
                !this.testator.enabledFeatures.includes(
                  'liquidity_calculator'
                ) &&
                (!this.testator.enabledFeatures.includes(
                  'advisor_full_insurance'
                ) ||
                  (this.testator.enabledFeatures.includes(
                    'advisor_full_insurance'
                  ) &&
                    ((this.$store.getters.isAdvisor &&
                      !this.$store.getters.isLoggedInAsSomeoneElse) ||
                      (!this.$store.getters.isAdvisor &&
                        (!testator.advisor ||
                          (testator.advisor &&
                            testator.advisor.company_default_advisor))))))
              "
              rounded
              depressed
              large
              color="primary"
              @click="returnToFreeQuote"
            >
              <v-icon small>{{ icons.mdiArrowLeft }}</v-icon
              ><span>Return</span>
            </v-btn>
            <GradientButton
              v-else
              title="Next"
              :small="true"
              @gradientButtonClicked="proceedToRequestAdvisorCall"
            >
              <template #append>
                <v-icon small>{{ icons.mdiArrowRight }}</v-icon>
              </template>
            </GradientButton>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import GradientButton from '../guest/GradientButton.vue';

  export default {
    name: 'EstimatedInsuranceRequiredOutput',
    components: {
      GradientButton,
    },
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        loading: true,
        estateCostCalculation: this.testator.estateCostCalculation,
      };
    },
    computed: {
      executorFeePercentage() {
        return this.estateCostCalculation.executorFeePercentage;
      },
      testatorAgeAndIdCorrect() {
        return (
          this.testator.age >= 18 &&
          this.testator.age <= 74 &&
          this.testator.identityNumber.idType !== 'PASSPORT'
        );
      },
    },
    mounted() {
      if (
        this.estateCostCalculation &&
        this.estateCostCalculation.estimatedEstateAdministrativeCosts
      ) {
        this.loading = false;
      } else {
        this.$router.push({ name: 'EstimatedInsuranceRequiredInput' });
      }
    },
    methods: {
      proceedToRequestAdvisorCall() {
        this.$router.push({ name: 'RequestAdvisorCall' });
      },
      returnToFreeQuote() {
        this.$router.push({ name: 'GetFreeInsuranceQuote' });
      },
      shouldShowNextButton() {
        const isAdvisor = this.$store.getters.isAdvisor;
        const hasAdvisorFullInsurance = this.testator.enabledFeatures.includes(
          'advisor_full_insurance'
        );

        return (
          !this.testatorAgeAndIdCorrect ||
          this.testator.enabledFeatures.includes('liquidity_calculator') ||
          (hasAdvisorFullInsurance &&
            this.$store.getters.isLoggedInAsSomeoneElse)
        );
      },
      next() {
        const isAdvisor = this.$store.getters.isAdvisor;
        const hasAdvisorFullInsurance = this.testator.enabledFeatures.includes(
          'advisor_full_insurance'
        );

        if (hasAdvisorFullInsurance && isAdvisor) {
          this.$router.push({ name: 'GetFreeInsuranceQuote' });
        } else if (this.testator.enabledFeatures.includes('full_insurance')) {
          if (this.testator.identityNumber.idType == 'PASSPORT') {
            this.$router.push({ name: 'InsuranceNonSACitizen' });
          } else if (this.testator.age >= 18 && this.testator.age <= 65) {
            this.$router.push({ name: 'EstimatedInsuranceRequired' });
          } else {
            this.$router.push({ name: 'InsuranceAgeRestricted' });
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .estimated-insurance-required-output-container {
    .secondary-highlighted {
      display: inline-block;
      padding: 8px 15px;
      font-weight: bold;
      font-size: 18px;
    }

    .total-cover-required {
      text-align: right;
    }

    .total-cover-required-xs {
      text-align: center;
    }

    .fee-block {
      margin: 12px 0;

      .fee-amount {
        float: right;
      }

      h3 {
        margin-bottom: 4px;
      }

      p {
        margin-left: 8px;
      }
    }
  }
</style>
